import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Button, Theme, Typography, makeStyles } from '@material-ui/core'
import AIIcon from 'resources/AIIcon'
import React from 'react'
import { SPEC_INT_PANEL_BACKGROUND_COLOR } from './specIntelligenceConstants'

/** Matches the AI purple used for the Insights tab icon and the prior AI Disclaimer dialog. */
const AI_ICON_COLOR = '#8A30BE'

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        flex: 1,
        overflow: 'auto',
        backgroundColor: SPEC_INT_PANEL_BACKGROUND_COLOR,
        paddingTop: theme.spacing(3),
        paddingBottom: theme.spacing(3),
        paddingLeft: theme.spacing(1.25),
        paddingRight: theme.spacing(1.25),
        gap: pxToRem(20),
    },
    // Sized to match ChatDisclaimerIcon's rendered footprint in the Dela disclaimer.
    icon: {
        width: pxToRem(64),
        height: pxToRem(64),
        color: AI_ICON_COLOR,
    },
    text: {
        fontFamily: 'Lexend',
        fontSize: pxToRem(14),
        fontWeight: 400,
        lineHeight: pxToRem(21),
        color: '#222222',
    },
    okButton: {
        borderRadius: '100px',
        borderWidth: '1px',
        borderStyle: 'solid',
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        fontSize: pxToRem(16),
        fontWeight: 500,
        fontFamily: 'Lexend',
        lineHeight: pxToRem(32),
    },
}))

export const SPEC_INTELLIGENCE_DISCLAIMER_MESSAGE =
    'This information is generated by an artificial intelligence tool exclusively using your Specpoint data and ' +
    'is subject to the Specpoint Product terms. AI‑generated outputs are not guaranteed to be accurate or complete ' +
    'and do not constitute representations or advice by Deltek. ' +
    'Deltek does not independently verify the accuracy of each output, and users are solely responsible for reviewing ' +
    'and validating them before any use. To report inaccurate or unexpected outputs, contact the Deltek Support Center. ' +
    'Copyright 2026 Deltek, Inc.'

/** Props for {@link SpecIntelligenceDisclaimer}. */
export interface SpecIntelligenceDisclaimerProps {
    /** Called when the user dismisses the disclaimer. */
    onDismiss: () => void
}

/** Inline AI disclaimer view shown in place of the drawer content, mirroring Dela's ChatDisclaimer. */
const SpecIntelligenceDisclaimer = ({ onDismiss }: SpecIntelligenceDisclaimerProps) => {
    const classes = useStyles()
    return (
        <div className={classes.container} data-testid="specIntelligenceDisclaimer">
            <AIIcon className={classes.icon} aria-hidden="true" />
            <Typography className={classes.text} data-testid="disclaimerText">
                {SPEC_INTELLIGENCE_DISCLAIMER_MESSAGE}
            </Typography>
            <Button
                id="hideSpecIntelligenceDisclaimerButton"
                data-testid="hideDisclaimerButton"
                type="button"
                aria-label="OK"
                color="primary"
                variant="outlined"
                size="large"
                className={classes.okButton}
                onClick={onDismiss}
            >
                OK
            </Button>
        </div>
    )
}

export default SpecIntelligenceDisclaimer
